// M13.1 — pure-string 100% stacked area SVG builder for the PDF.
//
// Mirrors the React SVG shape from `SourceMixChart.tsx` but emits a flat
// `<svg>...</svg>` string with no DOM, no React, no event handlers — safe
// to call from inside a Cloudflare Worker where DocRaptor's static-HTML
// pipeline picks it up. Same top-N + Other rollup as the dashboard chart
// so the two surfaces tell the same visual story.

import { extent, max, sum } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { area as d3Area, stack as d3Stack, stackOffsetExpand } from "d3-shape";

export interface SourceMixTrendRow {
  date: string;
  channel: string;
  sessions: number;
}

export interface BuildSourceMixSvgOpts {
  width?: number;
  height?: number;
  /** Same smart-default rule as the dashboard — informs x-tick density. */
  granularity: "day" | "week" | "month";
  /** Defaults to 8. Anything past this rolls into a single "Other" band. */
  topBandLimit?: number;
}

const DEFAULT_WIDTH = 680;
const DEFAULT_HEIGHT = 240;
const MARGIN = { top: 24, right: 16, bottom: 28, left: 44 };
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "#E5E7EB";
const LEGEND_TEXT = "#374151";
const OTHER_COLOR = "#A1A1AA"; // matches client OTHER_CHANNEL_COLOR
const OTHER_LABEL = "Other";

// Mirrors src/client/components/report/channel-colors.ts. Kept in this
// file (not imported) so the shared/PDF builder stays free of any client
// dependencies — the PDF runs in the Worker queue consumer.
const CHANNEL_COLOR: Record<string, string> = {
  Direct: "#0E1680",
  "Organic Search": "#7C5CFF",
  "Paid Search": "#F59E0B",
  "Organic Social": "#4F31FF",
  "Paid Social": "#9583FF",
  Referral: "#06B6D4",
  Email: "#A855F7",
  Display: "#F97316",
  Affiliates: "#EC4899",
  Audio: "#14B8A6",
  SMS: "#10B981",
  Video: "#EF4444",
  "Mobile Push Notifications": "#84CC16",
  "Cross-network": "#6366F1",
  Unassigned: "#71717A",
};

/**
 * Build a static SVG 100%-normalised stacked area chart for the PDF.
 * Returns the inline `<svg>...</svg>` string, ready to drop into the
 * print-html template.
 */
export function buildSourceMixSvg(rows: SourceMixTrendRow[], opts: BuildSourceMixSvgOpts): string {
  const width = opts.width ?? DEFAULT_WIDTH;
  const height = opts.height ?? DEFAULT_HEIGHT;
  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const topBandLimit = opts.topBandLimit ?? 8;

  if (rows.length === 0) {
    return emptyChart(width, height);
  }

  // ---- bucket the long tail into "Other" --------------------------------
  const totals = new Map<string, number>();
  const dateSet = new Set<string>();
  for (const r of rows) {
    totals.set(r.channel, (totals.get(r.channel) ?? 0) + r.sessions);
    dateSet.add(r.date);
  }
  const sortedChannels = Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([ch]) => ch);
  const topChannels = sortedChannels.slice(0, topBandLimit);
  const otherChannels = new Set(sortedChannels.slice(topBandLimit));
  const includeOther = otherChannels.size > 0;
  const keys = includeOther ? [...topChannels, OTHER_LABEL] : topChannels;

  const dates = Array.from(dateSet).sort();
  const cells: Record<string, number>[] = dates.map(() => {
    const r: Record<string, number> = {};
    for (const k of keys) r[k] = 0;
    return r;
  });
  const indexByDate = new Map(dates.map((d, i) => [d, i]));
  for (const r of rows) {
    const idx = indexByDate.get(r.date);
    if (idx === undefined) continue;
    const key = otherChannels.has(r.channel) ? OTHER_LABEL : r.channel;
    cells[idx][key] = (cells[idx][key] ?? 0) + r.sessions;
  }

  // ---- stack + scales ----------------------------------------------------
  const stackGen = d3Stack<Record<string, number>>().keys(keys).offset(stackOffsetExpand);
  const stacked = stackGen(cells);
  const dateObjs = dates.map((d) => new Date(`${d}T00:00:00Z`));
  const [xMin, xMax] = extent(dateObjs) as [Date, Date];
  const yMaxRaw = max(stacked, (s) => max(s, (d) => d[1])) ?? 1;
  const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
  const yScale = scaleLinear().domain([0, yMaxRaw]).range([innerH, 0]);

  const areaGen = d3Area<[number, number]>()
    .x((_, i) => xScale(dateObjs[i]))
    .y0((d) => yScale(d[0]))
    .y1((d) => yScale(d[1]));

  // ---- axis + gridlines --------------------------------------------------
  const yTickPercents = [0, 25, 50, 75, 100];
  const xTickCount = opts.granularity === "day" ? 6 : opts.granularity === "week" ? 5 : 4;
  const xTicks = xScale.ticks(xTickCount);

  const gridlines = yTickPercents
    .map((pct) => {
      const y = yScale(pct / 100);
      return (
        `<line x1="0" y1="${y}" x2="${innerW}" y2="${y}" stroke="${GRID_COLOR}" stroke-width="1" />` +
        `<text x="-8" y="${y + 3}" font-size="10" fill="${AXIS_COLOR}" text-anchor="end" font-family="inherit">${pct}%</text>`
      );
    })
    .join("");

  const xAxisTicks = xTicks
    .map((d) => {
      const x = xScale(d);
      return (
        `<line x1="${x}" y1="${innerH}" x2="${x}" y2="${innerH + 4}" stroke="${AXIS_COLOR}" stroke-width="1" />` +
        `<text x="${x}" y="${innerH + 16}" font-size="10" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">${escapeText(formatXTick(d, opts.granularity))}</text>`
      );
    })
    .join("");

  // ---- bands -------------------------------------------------------------
  const bands = stacked
    .map((s) => {
      // d3-stack returns SeriesPoint[] which extends [number, number]
      // with extra metadata; areaGen accepts the bare tuple shape.
      const tuples = s.map((p) => [p[0], p[1]] as [number, number]);
      const d = areaGen(tuples);
      if (!d) return "";
      const fill = s.key === OTHER_LABEL ? OTHER_COLOR : (CHANNEL_COLOR[s.key] ?? OTHER_COLOR);
      return `<path d="${d}" fill="${escapeAttr(fill)}" stroke="#ffffff" stroke-width="0.5" opacity="0.92" />`;
    })
    .join("");

  // ---- legend (chips above the plot) -------------------------------------
  // Chips wrap onto extra rows rather than running past the right edge,
  // where SVG content just clips. Each extra row grows the SVG and pushes
  // the plot down, so the plot keeps its size.
  const legendY = 10;
  const legendRowHeight = 14;
  const swatch = 8;
  let cursorX = MARGIN.left;
  let legendRow = 0;
  const chips: string[] = [];
  for (const k of keys) {
    const fill = k === OTHER_LABEL ? OTHER_COLOR : (CHANNEL_COLOR[k] ?? OTHER_COLOR);
    const total = k === OTHER_LABEL ? sumOther(rows, otherChannels) : (totals.get(k) ?? 0);
    const label = `${k} (${formatCount(total)})`;
    const textWidth = label.length * 5.5 + 6;
    const chipWidth = swatch + 4 + textWidth + 10;
    if (cursorX > MARGIN.left && cursorX + chipWidth > width - MARGIN.right) {
      cursorX = MARGIN.left;
      legendRow += 1;
    }
    const y = legendY + legendRow * legendRowHeight;
    chips.push(
      `<rect x="${cursorX}" y="${y - swatch + 2}" width="${swatch}" height="${swatch}" rx="1.5" fill="${escapeAttr(fill)}" />` +
        `<text x="${cursorX + swatch + 4}" y="${y + 2}" font-size="9" fill="${LEGEND_TEXT}" font-family="inherit">${escapeText(label)}</text>`,
    );
    cursorX += chipWidth;
  }
  const legend = chips.join("");
  const legendExtra = legendRow * legendRowHeight;
  const totalHeight = height + legendExtra;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${totalHeight}" width="${width}" height="${totalHeight}" role="img" aria-label="Source mix 100% stacked area">` +
    legend +
    `<g transform="translate(${MARGIN.left}, ${MARGIN.top + legendExtra})">` +
    gridlines +
    xAxisTicks +
    bands +
    `</g>` +
    `</svg>`
  );
}

function emptyChart(width: number, height: number): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Source mix (empty)">` +
    `<rect x="0" y="0" width="${width}" height="${height}" fill="#FAFAFA"></rect>` +
    `<text x="${width / 2}" y="${height / 2}" font-size="12" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">No traffic in window</text>` +
    `</svg>`
  );
}

function formatXTick(d: Date, granularity: "day" | "week" | "month"): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function formatCount(n: number): string {
  if (!isFinite(n)) return "0";
  if (Math.abs(n) >= 10_000) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
  }
  return new Intl.NumberFormat("en").format(Math.round(n));
}

function sumOther(rows: SourceMixTrendRow[], otherSet: Set<string>): number {
  return sum(rows, (r) => (otherSet.has(r.channel) ? r.sessions : 0));
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
